import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { setIsNewNotification } from '../../redux/userReducer';
import { getTransfers } from '../../services/portfolio';
import { NotificationContent } from 'components/Header/NotificationContainer';
import { useAuth } from 'react-oidc-context';

interface Holding {
  token_address: string;
  [key: string]: any;
}

interface Notification {
  id?: number;
  timestamp?: string | number;
  is_anomalous: boolean;
  anomaly_type: string;
  anomaly_details: string;
  historical_avg: string | number;
  z_score: string | number;
  [key: string]: any;
}

export const useNotifications = () => {
  const auth = useAuth();
  const dispatch = useDispatch();
  // const POLLING_INTERVAL = 60000; // 60 seconds
  const POLLING_INTERVAL = 120000; // 120 seconds
  
  const holdingsRef = useRef<Holding[]>([]);
  holdingsRef.current = useSelector((state) => state.portfolio.holdings || []);
  
  // Initialize with current timestamp - 5 minutes in seconds
  const lastCheckedRef = useRef(Math.floor((Date.now() - (5 * 60 * 1000)) / 1000));

  const showNotification = useCallback((notification: Notification) => {
    const toastId = toast(
      <NotificationContent notification={notification} isToast={true} />,
      {
        type: notification.is_anomalous ? 'warning' : 'info',
        autoClose: 15000,
        closeOnClick: false,
        pauseOnHover: true,
        style: {
          background: 'rgba(23, 23, 30, 0.95)',
          border: '1px solid rgba(115, 246, 255, 0.1)',
          boxShadow: '0 0 20px rgba(147, 51, 234, 0.05)',
          borderRadius: '12px',
          padding: '0',
          maxWidth: '580px',        // Keep this as your max boundary
          minWidth: '300px',        // Set a reasonable minimum
          width: 'auto',           // Allow natural expansion
          overflow: 'visible'      // Add this to prevent content cutoff
        },
        position: "top-right"
      }
    );
    
    dispatch(setIsNewNotification(true));
    return toastId;
  }, [dispatch]);

  const fetchNewNotifications = useCallback(async () => {
    try {
      if (!holdingsRef.current.length) {
        console.error('No holdings found, skipping transfer check');
        return;
      }

      const token_addresses = holdingsRef.current.map(holding => holding.token_address);
      const currentTimestamp = Math.floor(Date.now() / 1000);
      
      // Make sure auth.user exists before accessing access_token
      if (!auth.user?.access_token) {
        return;
      }

      const response = await getTransfers(auth.user.access_token, token_addresses);
      
      if (response && Array.isArray(response)) {
        const newTransfers = [];
        
        for (const transfer of response) {
          const transferTimestamp = parseInt(transfer.timestamp);
          
          // Only process transfers that happened in the past
          if (transferTimestamp > lastCheckedRef.current && transferTimestamp <= currentTimestamp) {
            newTransfers.push(transfer);
          } else {
            if (newTransfers.length > 0) {
              break;
            }
          }
        }
        
        if (newTransfers.length > 0) {
          // Filter transfers with z-score greater than 0.10
          const filteredTransfers = newTransfers.filter(transfer => {
            const zScore = parseFloat(transfer.z_score as string);
            return zScore > 2.00;
          });
          
          // Process filtered transfers in chronological order
          filteredTransfers.reverse().forEach(transfer => {
            showNotification(transfer);
          });
        }
      }
      
      // Update lastChecked to current timestamp in seconds
      lastCheckedRef.current = currentTimestamp;
    } catch (error) {
      console.error('Error fetching transfers:');
    }
  }, [auth.user?.access_token, showNotification]); // Added missing dependencies

  useEffect(() => {
    let isSubscribed = true;
    
    const pollForNotifications = async () => {
      if (isSubscribed) {
        await fetchNewNotifications();
      }
    };

    // Initial fetch
    pollForNotifications();
    
    // Set up polling interval
    const interval = setInterval(pollForNotifications, POLLING_INTERVAL);
    
    // Cleanup
    return () => {
      isSubscribed = false;
      clearInterval(interval);
    };
  }, [fetchNewNotifications, POLLING_INTERVAL]); // Added missing dependencies

  return { showNotification };
};

export default useNotifications;


